import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { isSubscribedReselector as stateSelector } from 'reduxModules/users/selectors'
import { changeSubscriptionDispatchers as dispatcher } from 'reduxModules/users/dispatchers'
import { LIFETIME_PLAN_ID } from 'reduxModules/subscriptions'
import SubscriptionPlanList from '../../SubscriptionPlans/SubscriptionPlanList'
import PLANS from '../../SubscriptionPlans/fixturePlans'
import './styles.css'

const COUPON_PLAN_QUERY = [
  ...PLANS
    .filter((p) => p.name !== 'Group Learning')
    .map((p) => ({ id: p.id, name: p.name, price: p.price })),
  { id: LIFETIME_PLAN_ID, name: 'Lifetime', price: 1199 }
]

const discountedPlanIds = (coupon) => {
  if (!coupon || !coupon.plans) return []
  return Object.keys(coupon.plans)
    .filter((key) => {
      const amount = parseFloat(coupon.plans[key].discountAmount)
      return amount > 0
    })
    .map(Number)
}

const applyCurrentPlanPrice = (plans, isSubscribed, subscription) => {
  if (!isSubscribed || !subscription) return plans
  const { planId, price } = subscription
  if (planId == null || price == null) return plans
  return plans.map((p) => {
    if (p.id !== planId) return p
    const isYearly = p.name === 'Yearly' || p.name === 'annual'
    const effectiveMonthly = isYearly
      ? (Number(price) / 12).toFixed(2)
      : Number(price).toFixed(2)
    return {
      ...p,
      price,
      priceMonthly: effectiveMonthly
    }
  })
}

export function ChangeSubscription(props) {
  const {
    isSubscribed,
    subscription = null,
    fetchUpgradeCoupon = () => Promise.resolve(null)
  } = props
  const location = useLocation()
  const codename = new URLSearchParams(location.search).get('codename') || ''

  const [coupon, setCoupon] = useState(null)
  const [couponError, setCouponError] = useState(false)
  const [couponLoading, setCouponLoading] = useState(Boolean(codename))

  useEffect(() => {
    if (!codename) {
      setCoupon(null)
      setCouponError(false)
      setCouponLoading(false)
      return
    }
    setCouponLoading(true)
    setCouponError(false)
    fetchUpgradeCoupon(codename, COUPON_PLAN_QUERY)
      .then((payload) => {
        setCoupon(payload)
        setCouponLoading(false)
      })
      .catch(() => {
        setCoupon(null)
        setCouponError(true)
        setCouponLoading(false)
      })
  }, [codename, fetchUpgradeCoupon])

  const currentRank = isSubscribed ? subscription.rank : 0
  const discountedIds = discountedPlanIds(coupon)
  // Plan-restricted coupon: exactly one plan has a discount. We treat that
  // plan as the target. For non-restricted coupons (every plan discounted)
  // we leave target null — no "already on target" short-circuit applies.
  const targetPlanId = discountedIds.length === 1 ? discountedIds[0] : null
  const alreadyOnTargetPlan =
    Boolean(coupon) &&
    targetPlanId != null &&
    isSubscribed &&
    subscription.planId === targetPlanId
  const couponApplicable =
    Boolean(coupon) &&
    discountedIds.some((id) => {
      const plan = PLANS.find((p) => p.id === id)
      return plan && plan.rank > currentRank
    })
  const upgradeCoupon = couponApplicable ? coupon : null
  const plansForDisplay = applyCurrentPlanPrice(
    PLANS,
    isSubscribed,
    subscription
  )

  const renderInstructionMessage = () => {
    if (upgradeCoupon) {
      const suffix = upgradeCoupon.discountType === 'pctg' ? '%' : ''
      const discountedPlanNames = discountedIds
        .map((id) => PLANS.find((p) => p.id === id))
        .filter(Boolean)
        .map((p) => p.name)
      const action = isSubscribed ? 'upgrade' : 'subscribe'
      if (discountedPlanNames.length === 1)
        return `Code ${upgradeCoupon.codename} applied — save ${upgradeCoupon.discount}${suffix} on the ${discountedPlanNames[0]} plan. Select it below to ${action}.`
      return `Code ${upgradeCoupon.codename} applied — save ${upgradeCoupon.discount}${suffix}. Select a new plan from the chart below to ${action}.`
    }
    return isSubscribed
      ? 'To upgrade your subscription, select a new plan from the chart below.'
      : 'To start a subscription, select a plan from the chart below.'
  }

  if (alreadyOnTargetPlan)
    return (
      <div
        id="ChangeSubscription"
        className="account-section wrapper"
      >
        <h3 className="account-section__title">upgrade subscription plan</h3>
        <div className="message coupon-already-applied">
          <span>
            You&rsquo;re already on the {subscription.plan} plan —
            you&rsquo;re all set! Manage your subscription in{' '}
            <Link to="/my-account">My Account</Link>.
          </span>
        </div>
      </div>
    )

  return (
    <div
      id="ChangeSubscription"
      className="account-section wrapper"
    >
      <h3 className="account-section__title">
        {isSubscribed ? 'upgrade subscription plan' : 'choose your subscription plan'}
      </h3>
      {couponError && (
        <div className="message coupon-error">
          <span>This promo has expired or is no longer available.</span>
        </div>
      )}
      <div className="message">
        <span>{renderInstructionMessage()}</span>
      </div>
      <div className="personal-information">
        {!couponLoading && (
          <SubscriptionPlanList
            plans={plansForDisplay}
            upgrading
            currentPlan={isSubscribed ? subscription.rank : 0}
            currentPlanName={isSubscribed ? subscription.plan : ''}
            currentPlanId={isSubscribed ? subscription.planId : ''}
            isSubscribed={isSubscribed}
            changeSubscription
            upgradeCoupon={upgradeCoupon}
            upgradeAction={(allPlans, newPlan, oldPlan) =>
              props.dispatchUpgradePlan(allPlans, newPlan, oldPlan, upgradeCoupon)
            }
          />
        )}
      </div>
    </div>
  )
}

ChangeSubscription.propTypes = {
  isSubscribed: PropTypes.bool.isRequired,
  subscription: PropTypes.shape({
    rank: PropTypes.number,
    plan: PropTypes.string,
    planId: PropTypes.number,
    price: PropTypes.number
  }),
  fetchUpgradeCoupon: PropTypes.func,
  dispatchUpgradePlan: PropTypes.func
}

export default connect(
  stateSelector,
  dispatcher
)(ChangeSubscription)
